import React from "react";
import { Button } from "@/shared/components/ui/button";
import { Undo2, CirclePlus, Send, Trash2 } from "lucide-react";
import styles from "./HeaderBar.module.css";

import { Timer } from "./Timer";
import { MatterSelector, type Matter } from "./MatterSelector";
import { DateRangePicker } from "./DateRangePicker";
import ConfirmableAction from "./ConfirmableAction";
import ApplicationFilter from "./ApplicationFilter";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { type FilterableAppId } from "../../hooks/useApplicationFilter";

// Action icons
import combineIcon from "@/assets/applications/CombineIcon.svg";
import combineIconWhite from "@/assets/applications/CombineIconWhite.svg";
import duplicateIcon from "@/assets/applications/DuplicateIcon.svg";
import duplicateIconWhite from "@/assets/applications/DuplicateIconWhite.svg";

type ViewMode = "entries" | "history" | "calendar" | "settings";

export interface HeaderBarProps {
  viewMode: ViewMode;
  date: Date;
  onDateChange: (d: Date) => void;
  dateRange?: { from: Date | undefined; to: Date | undefined };
  onDateRangeChange?: (r: { from: Date | undefined; to: Date | undefined }) => void;
  leftContent?: React.ReactNode;
  timerRunning: boolean;
  elapsedSeconds: number;
  onStart: () => void;
  onStop: () => void;
  onReset: () => void;
  selectionCount: number;
  onAddEntry: () => void;
  onCombine: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onUndo?: () => void;
  canUndo?: boolean;
  onExport: () => void;
  matters?: Matter[];
  hardcodedMatter?: string;
  onHardcodedMatterChange?: (matterId: string) => void;
  // Application filter props
  isApplicationVisible?: (appId: FilterableAppId) => boolean;
  onToggleApplication?: (appId: FilterableAppId) => void;
  isFilterActive?: boolean;
}

const HeaderBar: React.FC<HeaderBarProps> = ({
  viewMode,
  date,
  onDateChange,
  dateRange,
  onDateRangeChange,
  leftContent,
  timerRunning,
  elapsedSeconds,
  onStart,
  onStop,
  onReset,
  selectionCount,
  onAddEntry,
  onCombine,
  onDuplicate,
  onDelete,
  onUndo,
  canUndo = false,
  onExport,
  matters = [],
  hardcodedMatter = "",
  onHardcodedMatterChange,
  isApplicationVisible,
  onToggleApplication,
  isFilterActive = false,
}) => {
  const { darkMode } = useDarkMode();
  const isEntries = viewMode === "entries";
  const isHistory = viewMode === "history";
  const isCalendar = viewMode === "calendar";

  // Date navigation handlers
  const goToPreviousDay = () => {
    const prev = new Date(date);
    prev.setDate(prev.getDate() - 1);
    onDateChange(prev);
  };

  const goToNextDay = () => {
    const next = new Date(date);
    next.setDate(next.getDate() + 1);
    onDateChange(next);
  };

  return (
    <div className={styles.headerWrapper}>
      {/* ============================================
          TOP ROW: SummaryCards | Timer + MatterSelector
          ============================================ */}
      <div className={styles.topRow}>
        {/* Left: Summary Cards (40%) */}
        <div className={styles.topLeft}>
          {leftContent}
        </div>

        {/* Right: Timer + MatterSelector stacked (shown for entries and history) */}
        {(isEntries || isHistory) && (
          <div className={styles.topRight}>
            {/* Timer - 180px, left-aligned */}
            {!isCalendar && (
              <div className={styles.timerControl}>
                <Timer
                  elapsedSeconds={elapsedSeconds}
                  isRunning={timerRunning}
                  onStart={onStart}
                  onStop={onStop}
                  onReset={onReset}
                />
              </div>
            )}

            {/* Hardcoded Matter Selector - 180px */}
            {onHardcodedMatterChange && (
              <div className={styles.matterControl}>
                <MatterSelector
                  matters={matters}
                  value={hardcodedMatter}
                  onChange={onHardcodedMatterChange}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============================================
          BOTTOM ROW: ActionButtons+Matter | Timer+Add+Export
          ============================================ */}
      {isEntries && (
        <div className={styles.bottomRow}>
          {/* Left: Action Buttons (70%) */}
          <div className={styles.bottomLeft}>
            <TooltipProvider delayDuration={300}>
              <div className={styles.actionButtons}>
                {/* Filter - leftmost with 16px gap */}
                {isApplicationVisible && onToggleApplication && (
                  <ApplicationFilter
                    isVisible={isApplicationVisible}
                    onToggle={onToggleApplication}
                    isFilterActive={isFilterActive}
                  />
                )}

                {/* Combine, Duplicate, Delete, Undo - action icons */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={selectionCount === 0}
                        className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
                        onClick={onCombine}
                      >
                        <img
                          src={darkMode ? combineIconWhite : combineIcon}
                          alt="Combine"
                          className="h-4 w-4"
                        />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Combine</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={selectionCount === 0}
                        className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
                        onClick={onDuplicate}
                      >
                        <img
                          src={darkMode ? duplicateIconWhite : duplicateIcon}
                          alt="Duplicate"
                          className="h-4 w-4"
                        />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Duplicate</p>
                  </TooltipContent>
                </Tooltip>

                {/* Delete - destructive action with confirmation */}
                <ConfirmableAction
                  icon={<Trash2 className="h-4 w-4" />}
                  confirmText={() => `Delete ${selectionCount} ${selectionCount === 1 ? "entry" : "entries"}?`}
                  onConfirm={onDelete}
                  iconClassName={`${styles.actionButtonIcon} ${styles.deleteButton} disabled:opacity-40 disabled:cursor-not-allowed`}
                  confirmClassName={`${styles.actionButtonWide} ${styles.deleteConfirmButton}`}
                  tooltip="Delete"
                  resetTrigger={selectionCount}
                  disabled={selectionCount === 0}
                />

                {/* Undo - recovery action (temporarily hidden) */}
                {false && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="icon"
                        className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
                        onClick={onUndo}
                        disabled={!canUndo}
                      >
                        <Undo2 className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Undo</p>
                  </TooltipContent>
                </Tooltip>
                )}

                {/* Date Range Picker - compact with navigation */}
                <div className={styles.datePickerInline}>
                  <DateRangePicker
                    date={date}
                    onDateChange={onDateChange}
                    dateRange={dateRange}
                    onDateRangeChange={onDateRangeChange}
                    showPresets={isEntries}
                    compact
                    onPrevious={goToPreviousDay}
                    onNext={goToNextDay}
                  />
                </div>
              </div>
            </TooltipProvider>
          </div>

          {/* Right: Add + Export (30%) */}
          <div className={styles.bottomRight}>
            <div className={styles.primaryActions}>
              <Button
                variant="default"
                className="h-12 bg-[hsl(var(--brand-alt))] text-white hover:bg-[hsl(var(--brand-alt))]/90 gap-2.5 px-5 font-medium shadow-sm hover:shadow-md transition-shadow flex items-center justify-center"
                onClick={onAddEntry}
              >
                <CirclePlus className="h-[18px] w-[18px]" strokeWidth={1.5} />
                Add
              </Button>
              <Button
                variant="default"
                onClick={onExport}
                className="h-12 bg-[hsl(var(--accent-blue))] text-white hover:bg-[hsl(var(--accent-blue))]/90 gap-2.5 px-5 font-medium shadow-sm hover:shadow-md transition-shadow flex items-center justify-center"
              >
                <Send className="h-[18px] w-[18px]" />
                Export
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History view: Show Filter + DatePicker (no action buttons, no Add/Export) */}
      {isHistory && (
        <div className={styles.bottomRow}>
          <div className={styles.bottomLeft}>
            <TooltipProvider delayDuration={300}>
              <div className={styles.actionButtons}>
                {/* Filter */}
                {isApplicationVisible && onToggleApplication && (
                  <ApplicationFilter
                    isVisible={isApplicationVisible}
                    onToggle={onToggleApplication}
                    isFilterActive={isFilterActive}
                  />
                )}

                {/* Date Range Picker - no wrapper margin since it's directly after Filter */}
                <DateRangePicker
                  date={date}
                  onDateChange={onDateChange}
                  dateRange={dateRange}
                  onDateRangeChange={onDateRangeChange}
                  showPresets={false}
                  compact
                  onPrevious={goToPreviousDay}
                  onNext={goToNextDay}
                />
              </div>
            </TooltipProvider>
          </div>
          <div className={styles.bottomRight} />
        </div>
      )}
    </div>
  );
};

export default HeaderBar;
